<template>
  <div>
    <h2>
      {{ t('deliveryInformation') }}
    </h2>
    <div class="delivery-info position-relative">
      <div v-if="servicePoint">
        <p class="text-black-50 mb-1">
          {{ t('servicePoint') }}
        </p>
        <h4>{{ displayAddress }}</h4>
      </div>

      <p class="text-black-50 mb-1">
        {{ t('receiverAddress') }}
      </p>
      <!-- No postal code until the recipient has unlocked the tracking assistant with it (#1996). -->
      <div v-if="unlocked" class="delivery-info__recipient">
        <h4 class="mb-1">{{ recipientLines[0] }}</h4>
        <p v-for="line in recipientLines.slice(1)" :key="line" class="mb-0">{{ line }}</p>
        <p v-if="recipientContact" class="mb-0 mt-1 text-black-50">{{ recipientContact }}</p>
      </div>
      <h4 v-else>{{ [itemData.recipient.city, recipientCountry].filter(Boolean).join(', ') }}</h4>
    </div>

    <hr class="tracking-divider" />
  </div>
</template>

<script lang="ts">
import type { ServicePoint, TrackingData } from '@/types/Models/Shipment'
import { defineComponent, computed, type PropType, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { countryName, uniqueStrings } from '@/helpers/Utils'

export default defineComponent({
  name: 'DeliveryInformation',
  props: {
    itemData: {
      type: Object as PropType<TrackingData>,
      required: true,
    },
    // The recipient's postal code has been verified in the tracking assistant: show the full delivery address.
    unlocked: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { t, locale } = useI18n()

    const servicePoint: ComputedRef<ServicePoint | null> = computed(() => {
      return props.itemData.servicePoints?.[0] || null
    })

    const displayAddress: ComputedRef<string> = computed(() => {
      // "Name, Street 1, 123 45 City" (no country code: the service point is in the recipient's country)
      const place = [servicePoint.value?.postalCode, servicePoint.value?.city].filter(Boolean).join(' ')
      return uniqueStrings([servicePoint.value?.name, servicePoint.value?.addressLine, place]).join(', ')
    })

    const recipientCountry: ComputedRef<string> = computed(() =>
      countryName(props.itemData.recipient.countryCode, props.itemData.recipient.country, locale.value),
    )

    const recipientLines: ComputedRef<string[]> = computed(() => {
      const r = props.itemData.recipient
      const place = [r.postalCode, r.city].filter(Boolean).join(' ')
      return uniqueStrings([
        r.fullName || r.company,
        r.fullName ? r.company : null,
        r.addressLine,
        r.addressLine2,
        [place, recipientCountry.value].filter(Boolean).join(', '),
      ])
    })

    const recipientContact: ComputedRef<string> = computed(() =>
      uniqueStrings([props.itemData.recipient.phoneNumber, props.itemData.recipient.email]).join(' · '),
    )

    return {
      servicePoint,
      displayAddress,
      recipientLines,
      recipientContact,
      recipientCountry,
      t,
    }
  },
})
</script>
